import { FC, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { EntityResponse } from "../../../../api";
import { useClientEntityStats } from "../../../../api/hooks";
import { CandidateQualification, displayQualification } from "../../../../api/models/EntityCandidate";
import { Badge, Box, Card, CardBody, Flex, Grid, Heading, Icon, SimpleGrid, Text, VStack } from "@chakra-ui/react";
import { Shimmer } from "@am-tax/fe-core";
import { PercentPie } from "../../../ClientDashboardPage/PercentPie";
import { Bar, BarChart, CartesianGrid, Cell, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { IconBuildingWarehouse, IconClipboardCheck, IconShieldCheck, IconUsers } from "@tabler/icons-react";
import { EntityStats } from "../../../../api/hooks/useClientEntityStats";

// ── Application status chart entries (values come from the backend stats) ────
type StatusChartEntry = { label: string; valueKey: keyof EntityStats; color: string };
const STATUS_CHART_ENTRIES: StatusChartEntry[] = [
    { label: "Apps Submitted", valueKey: "totalScreened", color: "var(--chakra-colors-blue-800)" },
    { label: "Certifications Pending", valueKey: "certificationsPending", color: "var(--chakra-colors-cyan-500)" },
    { label: "Certifications Received", valueKey: "certificationsReceived", color: "var(--chakra-colors-green-500)" },
    { label: "Appeal Pending", valueKey: "appealPending", color: "var(--chakra-colors-purple-500)" },
    { label: "Appeal Denied", valueKey: "appealDenied", color: "var(--chakra-colors-orange-500)" },
];

// ── KPI stat card ─────────────────────────────────────────────────────────────
type StatCardProps = {
    title: string;
    value: number | string;
    sub?: string;
    accentColor: string;
    icon: FC<{ size?: number; strokeWidth?: number }>;
    isLoading: boolean;
    onClick?: () => void;
};

const StatCard: FC<StatCardProps> = ({ title, value, sub, accentColor, icon, isLoading, onClick }) => (
    <Shimmer isLoading={isLoading} height="100px">
        <Card
            variant="withShadow"
            overflow="hidden"
            h="100px"
            onClick={onClick}
            cursor={onClick ? "pointer" : undefined}
            _hover={onClick ? { boxShadow: "md", borderColor: "blue.200" } : undefined}
            transition="box-shadow 0.15s, border-color 0.15s"
        >
            <Box h="3px" bg={accentColor} />
            <CardBody pt={3} pb={3}>
                <Flex justify="space-between" align="flex-start">
                    <VStack align="flex-start" spacing={1}>
                        <Text fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase" letterSpacing="wider">
                            {title}
                        </Text>
                        <Text fontSize="2xl" fontWeight="bold" lineHeight={1} color="gray.800">
                            {value}
                        </Text>
                        {sub && (
                            <Text fontSize="xs" color="gray.500" mt={1}>
                                {sub}
                            </Text>
                        )}
                    </VStack>
                    <Icon as={icon} boxSize={6} color={accentColor} opacity={0.6} />
                </Flex>
            </CardBody>
        </Card>
    </Shimmer>
);

// ── Main component ────────────────────────────────────────────────────────────
type EntityOverviewTabProps = {
    clientId: string;
    entity: EntityResponse;
};

export const EntityOverviewTab: FC<EntityOverviewTabProps> = ({ clientId, entity }) => {
    const navigate = useNavigate();

    // All data comes from a single lightweight aggregation endpoint — no candidate rows transferred.
    const statsQuery = useClientEntityStats(clientId, entity.id, true);
    const stats = statsQuery.data;
    const isLoading = statsQuery.isLoading;

    const statusChartData = useMemo(
        () =>
            STATUS_CHART_ENTRIES.map(({ label, valueKey }) => ({
                name: label,
                value: (stats?.[valueKey] as number) ?? 0,
            })),
        [stats]
    );

    const qualChartData = useMemo(() => {
        return Object.entries(stats?.qualificationCounts ?? {})
            .map(([key, value]) => ({
                name: displayQualification(key as CandidateQualification),
                value,
            }))
            .sort((a, b) => b.value - a.value);
    }, [stats]);

    const hasQualData = qualChartData.length > 0;

    if (!isLoading && (stats?.totalCandidates ?? 0) === 0) {
        return (
            <Flex direction="column" align="center" justify="center" py={12} gap={2} color="gray.400">
                <Icon as={IconUsers} boxSize={10} />
                <Text fontSize="md" fontWeight="medium">
                    No candidates yet
                </Text>
                <Text fontSize="sm">Candidates who apply through this entity's survey link will appear here.</Text>
            </Flex>
        );
    }

    const total = stats?.totalCandidates ?? 0;
    const hired = stats?.totalHired ?? 0;
    const screened = stats?.totalScreened ?? 0;
    const eligible = stats?.totalEligible ?? 0;
    const questionnairesCompleted = stats?.questionnairesCompleted ?? 0;

    return (
        <VStack align="stretch" spacing="1.25rem">
            {/* KPI cards */}
            <SimpleGrid columns={{ base: 2, lg: 4 }} spacing="1rem">
                <StatCard
                    title="Total Candidates"
                    value={total}
                    accentColor="var(--chakra-colors-blue-500)"
                    icon={IconUsers}
                    isLoading={isLoading}
                    onClick={() => navigate(`/clients/${clientId}/candidates?eId=${entity.id}`)}
                />
                <StatCard
                    title="Hired"
                    value={hired}
                    sub={total > 0 ? `${Math.round((hired / total) * 100)}% hire rate` : undefined}
                    accentColor="var(--chakra-colors-teal-500)"
                    icon={IconBuildingWarehouse}
                    isLoading={isLoading}
                />
                <StatCard
                    title="Screened"
                    value={screened}
                    sub={total > 0 ? `out of ${total}` : undefined}
                    accentColor="var(--chakra-colors-cyan-500)"
                    icon={IconShieldCheck}
                    isLoading={isLoading}
                />
                <StatCard
                    title="Questionnaires"
                    value={questionnairesCompleted}
                    sub={total > 0 ? `${Math.round((questionnairesCompleted / total) * 100)}% completion` : undefined}
                    accentColor="var(--chakra-colors-purple-500)"
                    icon={IconClipboardCheck}
                    isLoading={isLoading}
                />
            </SimpleGrid>

            {/* Success rates + Application status */}
            <Grid templateColumns={{ base: "1fr", xl: "2fr 3fr" }} gap="1.25rem">
                <Card variant="withShadow">
                    <CardBody px="1.25rem" pt="1rem">
                        <Heading variant="cardHeading" mb=".5rem">
                            Success Rates
                        </Heading>
                        <Flex wrap="wrap" justify="space-around" gap={4} pt={2}>
                            <PercentPie
                                isLoading={isLoading}
                                color="var(--chakra-colors-blue-600)"
                                target={screened}
                                total={total}
                                title="Screened"
                                subtitle="[Screened] / [Total] × 100"
                                description="Screened candidates have advanced past the initial screening statuses."
                            />
                            <PercentPie
                                isLoading={isLoading}
                                color="var(--chakra-colors-cyan-500)"
                                target={eligible}
                                total={total}
                                title="Eligible"
                                subtitle="[Eligible] / [Total] × 100"
                                description="Eligible candidates have statuses not in: Not Screened, Screening, Opted Out, Not Eligible, Unable to Substantiate, Denied, Appeal Denied."
                            />
                            <PercentPie
                                isLoading={isLoading}
                                color="var(--chakra-colors-teal-500)"
                                target={hired}
                                total={total}
                                title="Hired"
                                subtitle="[Hired] / [Total] × 100"
                                description="Hired candidates have been actively employed at this location."
                            />
                        </Flex>
                    </CardBody>
                </Card>

                <Card variant="withShadow">
                    <CardBody px="1.25rem" pt="1rem">
                        <Heading variant="cardHeading" mb=".5rem">
                            Application Status
                        </Heading>
                        <Shimmer isLoading={isLoading} width="100%" height={240}>
                            <ResponsiveContainer width="100%" height={240}>
                                <BarChart data={statusChartData} layout="vertical" margin={{ top: 4, right: 48, left: 8, bottom: 4 }} barSize={16}>
                                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                                    <YAxis type="category" dataKey="name" width={155} tick={{ fontSize: 12 }} />
                                    <Tooltip cursor={{ fill: "var(--chakra-colors-gray-50)" }} />
                                    <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                                        {STATUS_CHART_ENTRIES.map(({ color }, i) => (
                                            <Cell key={i} fill={color} />
                                        ))}
                                        <LabelList dataKey="value" position="right" style={{ fontSize: 12, fontWeight: 600, fill: "#555" }} />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </Shimmer>
                    </CardBody>
                </Card>
            </Grid>

            {/* WOTC category breakdown */}
            {(isLoading || hasQualData) && (
                <Card variant="withShadow">
                    <CardBody px="1.25rem" pt="1rem" pb="1.5rem">
                        <Heading variant="cardHeading" mb=".5rem">
                            WOTC Categories
                        </Heading>
                        <Shimmer isLoading={isLoading} width="100%" height={Math.max(160, qualChartData.length * 36 + 20)}>
                            <ResponsiveContainer width="100%" height={Math.max(160, qualChartData.length * 36 + 20)}>
                                <BarChart data={qualChartData} layout="vertical" margin={{ top: 4, right: 48, left: 8, bottom: 4 }} barSize={20}>
                                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                                    <YAxis type="category" dataKey="name" width={240} tick={{ fontSize: 12 }} />
                                    <Tooltip cursor={{ fill: "var(--chakra-colors-gray-50)" }} />
                                    <Bar dataKey="value" fill="var(--chakra-colors-blue-400)" radius={[0, 4, 4, 0]}>
                                        <LabelList dataKey="value" position="right" style={{ fontSize: 12, fontWeight: 600, fill: "#555" }} />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </Shimmer>
                        {!isLoading && (
                            <Flex gap={2} flexWrap="wrap" mt={3}>
                                {qualChartData.map(({ name, value }) => (
                                    <Badge key={name} colorScheme="blue" variant="subtle" borderRadius="full" px={2}>
                                        {name}: {value}
                                    </Badge>
                                ))}
                            </Flex>
                        )}
                    </CardBody>
                </Card>
            )}
        </VStack>
    );
};
